import { useQuery } from '@apollo/client/react';
import { monoFont } from '@medcom/shared-ui/styles/globalCss.ts';
import { antdTokenFilter, matchesTokens, tokenize } from '@medcom/shared-ui/utils/tokenSearch.ts';
import { Button, Checkbox, Col, Divider, Form, type FormInstance, InputNumber, Row, Select, Tabs, Transfer, Typography } from 'antd';
import type React from 'react';
import { LoadingIndicator } from '../../../../components/LoadingIndicator.tsx';
import { ParameterInsuranceEditor } from '../../../../components/ParameterInsuranceEditor';
import { SpecimenSelect } from '../../../../components/SpecimenSelect';
import type { CatalogParameterUpdateInput } from '../../../../graphql/generated/graphql.ts';
import { CATALOG_PARAMETER_FORM_QUERY } from './CatalogParameterForm.gql';

export const CatalogParameterForm: React.FC<{
  form: FormInstance<CatalogParameterUpdateInput>;
  labId: string;
  onFinish: (values: CatalogParameterUpdateInput) => void;
}> = ({ form, labId, onFinish }) => {
  const { data, loading } = useQuery(CATALOG_PARAMETER_FORM_QUERY, {
    variables: {
      labId: labId,
    },
    skip: !labId,
  });

  if (loading) {
    return <LoadingIndicator height="200px" />;
  }

  return (
    <Form layout="vertical" form={form} onFinish={onFinish}>
      <Tabs
        defaultActiveKey="grunddaten"
        size="small"
        items={[
          {
            key: 'grunddaten',
            label: 'Grunddaten',
            forceRender: true,
            children: (
              <>
                <Divider titlePlacement="left">Privat an Zuweiser</Divider>
                <Row gutter={[16, 0]}>
                  <Col span={6}>
                    <Form.Item name="doctorBilling" valuePropName="checked">
                      <Checkbox>Anforderbar</Checkbox>
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item name="useDoctorBilling" valuePropName="checked">
                      <Checkbox>Überschreiben</Checkbox>
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name="priceDoctor"
                      label="Preis Zuweiser"
                      rules={[
                        {
                          type: 'number',
                          required: false,
                          min: 0,
                          max: 9999.99,
                          message: 'Bitte validen Betrag angeben',
                        },
                      ]}
                    >
                      <InputNumber
                        inputMode="decimal"
                        precision={2}
                        decimalSeparator=","
                        min={0}
                        max={9999.99}
                        step={0.01}
                        suffix="€"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item name="usePriceDoctor" valuePropName="checked">
                      <Checkbox>Überschreiben</Checkbox>
                    </Form.Item>
                  </Col>
                </Row>
                <Divider titlePlacement="left">Privat an Patient</Divider>
                <Row gutter={[16, 0]}>
                  <Col span={6}>
                    <Form.Item name="patientBilling" valuePropName="checked">
                      <Checkbox>Anforderbar</Checkbox>
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item name="usePatientBilling" valuePropName="checked">
                      <Checkbox>Überschreiben</Checkbox>
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name="pricePatient"
                      label="Preis Patient"
                      rules={[
                        {
                          type: 'number',
                          required: false,
                          min: 0,
                          max: 9999.99,
                          message: 'Bitte validen Betrag angeben',
                        },
                      ]}
                    >
                      <InputNumber
                        inputMode="decimal"
                        precision={2}
                        decimalSeparator=","
                        min={0}
                        max={9999.99}
                        step={0.01}
                        suffix="€"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item name="usePricePatient" valuePropName="checked">
                      <Checkbox>Überschreiben</Checkbox>
                    </Form.Item>
                  </Col>
                </Row>
                <Divider titlePlacement="left">LG - Privat an Zuweiser</Divider>
                <Row gutter={[16, 0]}>
                  <Col span={6}>
                    <Form.Item name="lgDoctorBilling" valuePropName="checked">
                      <Checkbox>Anforderbar</Checkbox>
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item name="useLgDoctorBilling" valuePropName="checked">
                      <Checkbox>Überschreiben</Checkbox>
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name="priceLgDoctor"
                      label="Preis LG-Zuweiser"
                      rules={[
                        {
                          type: 'number',
                          required: false,
                          min: 0,
                          max: 9999.99,
                          message: 'Bitte validen Betrag angeben',
                        },
                      ]}
                    >
                      <InputNumber
                        inputMode="decimal"
                        precision={2}
                        decimalSeparator=","
                        min={0}
                        max={9999.99}
                        step={0.01}
                        suffix="€"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item name="usePriceLgDoctor" valuePropName="checked">
                      <Checkbox>Überschreiben</Checkbox>
                    </Form.Item>
                  </Col>
                </Row>
                <Divider titlePlacement="left">Preis Selbstbehalt</Divider>
                <Row gutter={[16, 0]}>
                  <Col span={12}></Col>
                  <Col span={6}>
                    <Form.Item
                      name="priceDeductible"
                      label="Preis Selbstbehalt"
                      rules={[
                        {
                          type: 'number',
                          required: false,
                          min: 0,
                          max: 9999.99,
                          message: 'Bitte validen Betrag angeben',
                        },
                      ]}
                    >
                      <InputNumber
                        inputMode="decimal"
                        precision={2}
                        decimalSeparator=","
                        min={0}
                        max={9999.99}
                        step={0.01}
                        suffix="€"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item name="usePriceDeductible" valuePropName="checked">
                      <Checkbox>Überschreiben</Checkbox>
                    </Form.Item>
                  </Col>
                </Row>
                <Divider titlePlacement="left">Material</Divider>
                <Form.Item name="specimenIds" valuePropName="selected">
                  <SpecimenSelect style={{ width: '100%' }} multiple specimens={data?.labSpecimens ?? []} />
                </Form.Item>
                <Form.Item name="useSpecimens" valuePropName="checked">
                  <Checkbox>Überschreiben</Checkbox>
                </Form.Item>
                <Divider titlePlacement="left">Formulare</Divider>
                <Form.Item name="formIds">
                  <Select
                    showSearch={{ filterOption: antdTokenFilter }}
                    allowClear
                    mode="multiple"
                    placeholder="Suche nach Formularen"
                    options={[...(data?.labForms ?? [])]
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(it => ({
                        label: it.name,
                        value: it.id,
                        search: it.name,
                      }))}
                  />
                </Form.Item>
                <Form.Item name="useForms" valuePropName="checked">
                  <Checkbox>Überschreiben</Checkbox>
                </Form.Item>
              </>
            ),
          },
          {
            key: 'krankenkassen',
            label: 'Krankenkassen',
            forceRender: true,
            children: (
              <>
                <Form.Item name="useInsuranceDefinitions" valuePropName="checked">
                  <Checkbox>Überschreiben</Checkbox>
                </Form.Item>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, curValues) => prevValues.insuranceDefinitions !== curValues.insuranceDefinitions}
                >
                  {() => {
                    return (
                      <ParameterInsuranceEditor
                        periodicGroups={data?.labPeriodicGroups ?? []}
                        insurances={data?.insurances ?? []}
                        diagnoses={data?.labDiagnoses ?? []}
                        lg={false}
                      />
                    );
                  }}
                </Form.Item>
              </>
            ),
          },
          {
            key: 'lg-krankenkassen',
            label: 'LG-Krankenkassen',
            forceRender: true,
            children: (
              <>
                <Form.Item name="useLgInsuranceDefinitions" valuePropName="checked">
                  <Checkbox>Überschreiben</Checkbox>
                </Form.Item>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, curValues) => prevValues.insuranceDefinitions !== curValues.insuranceDefinitions}
                >
                  {() => {
                    return (
                      <ParameterInsuranceEditor
                        periodicGroups={data?.labPeriodicGroups ?? []}
                        insurances={data?.insurances ?? []}
                        diagnoses={data?.labDiagnoses ?? []}
                        lg={true}
                      />
                    );
                  }}
                </Form.Item>
              </>
            ),
          },
          {
            key: 'sondertarife',
            label: 'Sondertarife',
            forceRender: true,
            children: (
              <>
                <Form.Item name="useSpecialRates" valuePropName="checked">
                  <Checkbox>Überschreiben</Checkbox>
                </Form.Item>
                <Form.Item name="specialRateIds" valuePropName="targetKeys">
                  <Transfer
                    dataSource={[...(data?.labSpecialRates ?? [])].sort((a, b) => a.shortName.localeCompare(b.shortName))}
                    titles={['Nicht anforderbar', 'Anforderbar']}
                    render={sr => (
                      <>
                        {sr.shortName}{' '}
                        <Typography.Text type="secondary">
                          <span className={monoFont}>{sr.code}</span>
                        </Typography.Text>
                      </>
                    )}
                    showSearch
                    filterOption={(inputValue, specialRate) =>
                      matchesTokens(specialRate, tokenize(inputValue), [sr => sr.name, sr => sr.shortName])
                    }
                    styles={{ section: { height: '300px', width: '100%', overflow: 'hidden' } }}
                    rowKey={sr => sr.id}
                  />
                </Form.Item>
              </>
            ),
          },
        ]}
      />
      {/* hack for enter button support */}
      <Button htmlType="submit" hidden />
    </Form>
  );
};
